
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { buildOwnProfileInsert } from "@/utils/clientWrites";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const { data: { session }, error: authError } = await supabase.auth.getSession();

        if (authError) throw authError;

        if (!session) {
          throw new Error("Não foi possível realizar o login. Tente novamente.");
        }

        // Verificar se o usuário tem perfil e qual é o seu papel
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .maybeSingle();
        
        if (profileError) {
          console.error("Erro ao verificar perfil:", profileError);
        }
        
        // Se não tem perfil, criar um
        if (!profile) {
          const { error: insertError } = await supabase
            .from("profiles")
            .insert(buildOwnProfileInsert({
              id: session.user.id,
              email: session.user.email || "",
            }));
            
          if (insertError) {
            console.error("Erro ao criar perfil:", insertError);
          }
        }

        toast({
          title: "Login realizado com sucesso!",
          description: "Redirecionando para o painel...",
        });

        // Redirecionar imediatamente para o admin
        navigate("/admin", { replace: true });

      } catch (err) {
        console.error("Erro na autenticação:", err);
        setError(err instanceof Error ? err.message : "Ocorreu um erro durante o login");
        
        setTimeout(() => {
          navigate("/entrar");
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [navigate, toast]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <span className="bg-blue-100 rounded-full p-3">
              <Mail className="w-6 h-6 text-blue-600" />
            </span>
          </div>
          
          {error ? (
            <>
              <h2 className="text-2xl font-bold text-red-600">
                Ops! Algo deu errado
              </h2>
              <p className="mt-2 text-gray-600">
                {error}
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Redirecionando para a página de login...
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900">
                Autenticando...
              </h2>
              <p className="mt-2 text-gray-600">
                Aguarde enquanto finalizamos seu login
              </p>
            </>
          )}
        </div>

        <div className="flex justify-center">
          <div className="mt-4 animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </div>
    </div>
  );
}
